import classNames from 'classnames';
import dayjs from 'dayjs';
import { memo, useState } from 'react';
import { MdPallet } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, Icon, Table, Tooltip, Whisper } from 'rsuite';
import { TableProps } from 'rsuite/lib/Table';
import { GerenciadorPrecos } from '../../../@types/GerenciadorPrecos';
import { RootState } from '../../../@types/RootState';
import { CustomHeaderCell } from '../../../components';
import { GerenciadorDatakeysState, SET_GET_CLUSTER_STORE_MODAL_IS_OPEN } from '../../../reducers';
import { useFarma } from '../../Administrador/Produtos/Components/SetupFarma/hooks/useFarma';
import {
  BaseCell,
  CompetitorPriceCell,
  ExpandOfferCell,
  HoverNomeColuna,
  MargemCell,
  ObjectiveMarginCell,
  PMZCell,
  PrecoSugeridoCell,
  PrecoVigenteCell,
  ProductCell,
} from './Components';
import { GetClusterListStoreModal } from './Components/GetClusterStoreListModal';
import { GetStoreFamilyClusterModal } from './Components/GetStoreFamilyClusterListModal';
import { CpiCell } from './Components/TableCells/CpiCell';
import DemandForecastCell from './Components/TableCells/DemandForecastCell/DemandForecastCell';
import { ExpandWholesaleCell } from './Components/TableCells/ExpandWholesaleCell';
import PmcPriceTag from './Components/TableCells/PMCCell/PMCCel';
import styles from './gerenciadorPrecos.module.scss';
import { getSortOptions, getSortOptionValue } from './sortOptions';
import { getBrokenLimitFilterValue } from './utils/brokenLimitWarningUtils';

const { Cell, Column, HeaderCell } = Table;

const MemoCheckbox = memo(Checkbox);
const MemoCompetitorPriceCell = memo(CompetitorPriceCell);
const MemoExpandWholesaleCell = memo(ExpandWholesaleCell);
const MemoExpandOfferCell = memo(ExpandOfferCell);
const MemoProductCell = memo(ProductCell);
const MemoPmcPriceTag = memo(PmcPriceTag);

type RowData = GerenciadorPrecos.RowData;
type DataKey = GerenciadorPrecos.DataKey;
type RowHeight = number | ((rowData?: RowData) => number);

type TableGerenciadorProps = Omit<TableProps<DataKey, RowData>, 'rowHeight'> & {
  datakeys: GerenciadorDatakeysState;
  isAllChecked: boolean;
  isIndeterminate: boolean;
  expandedRowKeys: string[];
  rowHeight?: RowHeight;
  onChangeCheckAll: (checked: boolean) => void;
  handleCheck: (value: DataKey, checked: boolean) => void;
  handleBlur: (rowData: RowData, index: number) => void;
  handleRestoreRow: (rowData: RowData, index: number) => void;
  handleUpdateCampo: (name: GerenciadorPrecos.Inputs, value: number, rowData: RowData, index: number) => void;
  isToggleChecked: (productsToBePricedId: string) => boolean;
  onUpdateSortColumn?: (dataKey: string, value: string, option?: { sortValue?: string; priceType?: 'REGULAR' | 'OFFER' }) => void;
  sortPriceType?: 'REGULAR' | 'OFFER';
  getSelectedSortOptionValue?: typeof getSortOptionValue;
};

const TableGerenciador = ({
  datakeys,
  isAllChecked,
  isIndeterminate,
  expandedRowKeys,
  sortColumn,
  onChangeCheckAll,
  handleBlur,
  handleRestoreRow,
  handleCheck,
  handleUpdateCampo,
  isToggleChecked,
  onSortColumn,
  onUpdateSortColumn,
  sortPriceType = 'REGULAR',
  getSelectedSortOptionValue = getSortOptionValue,
  rowHeight,
  ...props
}: TableGerenciadorProps) => {
  const { configFarma } = useFarma();
  const hasVariableExpandedRows = typeof rowHeight === 'function';

  const datapointExhibitionType = useSelector((state: RootState) => {
    return state.datapointExhibitionType.datapointExhibitionType;
  });

  const isPriceOfferEnabled = useSelector((state: RootState) => {
    return state.reducerIPA.channels.offer.enabled;
  });

  const brokenLimitFilter = useSelector((state: RootState) => {
    return getBrokenLimitFilterValue(state.gerenciadorPrecosReducer.quickActionsFilters.brokenLimit);
  });

  const isFarmaClient = useSelector((state: RootState) => {
    return state.reducerIPA.basicConfig?.competitorsDays.offer_channels_config_dto?.enabled ?? false;
  });

  const dispatch = useDispatch();
  const sortOptions = getSortOptions(isFarmaClient);
  const getActiveSelectedValue = (column: Parameters<typeof getSortOptionValue>[0]) => {
    return getSelectedSortOptionValue(column, sortColumn as string, sortPriceType);
  };

  const isShowCountStoreCluster = datapointExhibitionType === 'PRODUCT_CLUSTER' || datapointExhibitionType === 'PRODUCT_CLUSTER_FAMILY';

  const [showModalClusterStore, setShowModalClusterStore] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<RowData | null>(null);
  const [showModalProductFamilyCluster, setShowModalProductFamilyCluster] = useState(false);

  const handleOpenModal = (rowData: RowData) => {
    if (datapointExhibitionType === 'PRODUCT_CLUSTER') {
      setSelectedRowData(rowData);
      setShowModalClusterStore(true);
      dispatch(SET_GET_CLUSTER_STORE_MODAL_IS_OPEN(true));
    }
    if (datapointExhibitionType === 'PRODUCT_CLUSTER_FAMILY') {
      setSelectedRowData(rowData);
      setShowModalProductFamilyCluster(true);
      dispatch(SET_GET_CLUSTER_STORE_MODAL_IS_OPEN(true));
    }
  };

  return (
    <>
      <Table
        id="table-revisao-precos"
        className={classNames(
          'table',
          styles['gerenciador-page__table'],
          hasVariableExpandedRows && styles['gerenciador-page__table--variable-expanded-rows'],
        )}
        rowClassName={(rowData: RowData) => {
          return classNames('revisao-row', styles['gerenciador-page__table__row'], {
            [styles['gerenciador-page__table__row--red']]:
              rowData?.pmc !== null && rowData?.price !== null && rowData?.pmc < rowData?.price && configFarma?.usePMCForIPAPricing,
          });
        }}
        expandedRowKeys={expandedRowKeys}
        autoHeight
        onSortColumn={onSortColumn}
        sortColumn={sortColumn}
        {...props}
        rowHeight={rowHeight as TableProps<DataKey, RowData>['rowHeight']}
      >
        {/* @ts-ignore */}
        <Column align="center" width={60}>
          <HeaderCell>
            <MemoCheckbox
              checked={isAllChecked}
              indeterminate={isIndeterminate}
              onChange={(_, checked) => {
                onChangeCheckAll(checked);
              }}
            />
          </HeaderCell>
          <Cell>
            {(rowData: RowData) => {
              return (
                <BaseCell className={styles['gerenciador-page__check-cell']} rowData={rowData}>
                  <MemoCheckbox
                    value={rowData.productsToBePricedId}
                    onChange={(value: DataKey, checked) => {
                      handleCheck(value, checked);
                    }}
                    checked={isToggleChecked(rowData.productsToBePricedId)}
                  />
                </BaseCell>
              );
            }}
          </Cell>
        </Column>

        {/* @ts-ignore */}
        <Column sortable flexGrow={2} minWidth={140}>
          <HeaderCell>
            <CustomHeaderCell
              dataKey={datakeys.productId}
              value={sortColumn}
              selectedValue={getActiveSelectedValue('productId')}
              data={sortOptions.productId}
              onChangeSort={onUpdateSortColumn}
            >
              Produto
            </CustomHeaderCell>
          </HeaderCell>
          <Cell dataKey={datakeys.productId}>{(rowData: RowData) => <MemoProductCell rowData={rowData} />}</Cell>
        </Column>

        {/* @ts-ignore */}
        <Column sortable width={100}>
          <HeaderCell>Loja</HeaderCell>
          <Cell dataKey="storeId">
            {(rowData: RowData) => {
              return (
                <BaseCell rowData={rowData}>
                  {rowData.storeName ? (
                    <Whisper
                      delayShow={0.2}
                      trigger="hover"
                      placement="right"
                      speaker={<Tooltip>{datapointExhibitionType === 'PRODUCT_CLUSTER' ? rowData?.clusterName : rowData?.storeName}</Tooltip>}
                    >
                      <span className="font-size-100-semibold">
                        {datapointExhibitionType === 'PRODUCT_CLUSTER' || datapointExhibitionType === 'PRODUCT_CLUSTER_FAMILY'
                          ? rowData?.clusterItemsCount
                            ? rowData?.clusterName
                            : rowData?.storeId
                          : rowData?.storeId}
                        <br />
                        {isShowCountStoreCluster && !!rowData?.clusterItemsCount && (
                          <span className={styles['store-cluster-count']} onClick={() => handleOpenModal(rowData)}>
                            {rowData?.clusterItemsCount} {rowData?.clusterItemsCount === 1 ? 'LOJA' : 'LOJAS'}
                            {rowData.hasEditedProducts === true && (
                              <Whisper
                                placement="bottom"
                                speaker={
                                  <Tooltip className={styles['custom-tooltip']}>
                                    Algumas lojas deste cluster tiveram preços editados e podem estar diferentes do esperado
                                  </Tooltip>
                                }
                              >
                                <Icon className={styles['store-cluster-count-warning']} icon="exclamation-triangle" />
                              </Whisper>
                            )}
                          </span>
                        )}
                      </span>
                    </Whisper>
                  ) : null}
                </BaseCell>
              );
            }}
          </Cell>
        </Column>

        {/* @ts-ignore */}
        <Column sortable width={120}>
          <HeaderCell>Estoque</HeaderCell>
          <Cell dataKey="stock_quantity">
            {(rowData: RowData) => {
              return (
                <BaseCell rowData={rowData}>
                  <div className={styles['estoque-venda-container']}>
                    <div className={styles['estoque-venda-top']}>
                      <span className={styles['estoque-venda-icon']}>
                        <MdPallet width={10} height={10} />
                      </span>
                      <Whisper placement="top" speaker={<Tooltip>Estoque</Tooltip>}>
                        <span className={styles['estoque-venda-numero']}>{rowData.stock?.quantity ?? 0}</span>
                      </Whisper>
                    </div>
                    <div className={styles['estoque-venda-bottom']}>
                      <Whisper placement="left" speaker={<Tooltip>Dias de estoque</Tooltip>}>
                        <span className={styles['estoque-venda-badge']}>
                          {rowData.stock?.coverageDays ? `${Math.round(rowData.stock.coverageDays)}d` : '-'}
                        </span>
                      </Whisper>
                      <Whisper placement="right" speaker={<Tooltip>Data última entrada do produto</Tooltip>}>
                        <span className={styles['estoque-venda-data']}>
                          {rowData.stock?.lastUpdated && dayjs(rowData.stock.lastUpdated).isValid()
                            ? dayjs(rowData.stock.lastUpdated).format('DD/MM/YYYY')
                            : '--'}
                        </span>
                      </Whisper>
                    </div>
                  </div>
                </BaseCell>
              );
            }}
          </Cell>
        </Column>

        {/* @ts-ignore */}
        <Column sortable width={88}>
          <HeaderCell className={styles['gerenciador-page__table__header-cell']}>
            <div>
              <p>PMZ e</p>
              <p>custos</p>
            </div>
          </HeaderCell>
          <Cell dataKey="pmz">{(rowData: RowData) => <PMZCell rowData={rowData} />}</Cell>
        </Column>

        {/* @ts-ignore */}
        <Column sortable width={78}>
          <HeaderCell className={styles['gerenciador-page__table__header-cell']}>
            <div>
              <p>Margem</p>
              <p>Objetiva</p>
            </div>
          </HeaderCell>
          <Cell dataKey="objectiveMargin">{(rowData: RowData) => <ObjectiveMarginCell rowData={rowData} />}</Cell>
        </Column>

        {configFarma?.usePMCForIPAPricing && (
          // @ts-ignore
          <Column sortable width={124}>
            <HeaderCell className={styles['gerenciador-page__table__header-cell']}>
              <div>
                <p>PMC</p>
              </div>
            </HeaderCell>
            <Cell dataKey="pmc">
              {(rowData: RowData) => {
                return (
                  <MemoPmcPriceTag
                    className={classNames({
                      [styles.redBackground]: rowData?.pmc !== null && rowData?.price !== null && rowData?.pmc < rowData?.price,
                    })}
                    pmcPrice={rowData?.pmc}
                    date={rowData?.pmcLastDate ?? ''}
                    rowData={rowData}
                  />
                );
              }}
            </Cell>
          </Column>
        )}

        {/* @ts-ignore */}
        <Column sortable width={124}>
          <HeaderCell className={styles['gerenciador-page__table__header-cell']}>
            {isFarmaClient ? (
              <CustomHeaderCell
                dataKey="priceRetail"
                value={sortColumn}
                selectedValue={getActiveSelectedValue('priceRetail')}
                data={sortOptions.priceRetail}
                onChangeSort={onUpdateSortColumn}
              >
                <div>
                  <p>Preço</p>
                  <p>vigente</p>
                </div>
              </CustomHeaderCell>
            ) : (
              <div>
                <p>Preço</p>
                <p>vigente</p>
              </div>
            )}
          </HeaderCell>
          <Cell dataKey="priceRetail">{(rowData: RowData) => <PrecoVigenteCell rowData={rowData} />}</Cell>
        </Column>

        {/* @ts-ignore */}
        <Column sortable width={127}>
          <HeaderCell className={styles['gerenciador-page__table__header-cell']}>
            {isFarmaClient ? (
              <CustomHeaderCell
                dataKey="competitorsPrice"
                value={sortColumn}
                selectedValue={getActiveSelectedValue('competitorsPrice')}
                data={sortOptions.competitorsPrice}
                onChangeSort={onUpdateSortColumn}
              >
                <div>
                  <p>Preço</p>
                  <p>concorrente</p>
                </div>
              </CustomHeaderCell>
            ) : (
              <div>
                <p>Preço</p>
                <p>concorrente</p>
              </div>
            )}
          </HeaderCell>
          <Cell dataKey="competitorsPrice">{(rowData: RowData) => <MemoCompetitorPriceCell rowData={rowData} />}</Cell>
        </Column>

        {/* @ts-ignore */}
        <Column sortable width={132}>
          <HeaderCell>
            <CustomHeaderCell
              dataKey={datakeys.price}
              value={sortColumn}
              selectedValue={getActiveSelectedValue('price')}
              data={sortOptions.price}
              onChangeSort={onUpdateSortColumn}
            >
              Preço sugerido
            </CustomHeaderCell>
          </HeaderCell>
          <Cell className="table__input-group" dataKey={datakeys.price}>
            {(rowData: RowData, index: number) => {
              return (
                <PrecoSugeridoCell
                  dataKey="novoPreco"
                  rowData={rowData}
                  index={index}
                  brokenLimitFilter={brokenLimitFilter}
                  handleUpdateCampo={handleUpdateCampo}
                  handleBlur={(rowData: RowData) => handleBlur(rowData, index)}
                />
              );
            }}
          </Cell>
        </Column>

        {/* @ts-ignore */}
        <Column sortable width={116}>
          <HeaderCell>
            <CustomHeaderCell
              dataKey={datakeys.cpi}
              value={sortColumn}
              selectedValue={getActiveSelectedValue('cpi')}
              data={sortOptions.cpi}
              onChangeSort={onUpdateSortColumn}
            >
              <HoverNomeColuna placement="bottom" text="CPI" tooltip="Competitividade" />
            </CustomHeaderCell>
          </HeaderCell>
          <Cell dataKey={datakeys.cpi}>
            {(rowData: RowData, index: number) => {
              return (
                <CpiCell
                  dataKey="novaCompetitividade"
                  rowData={rowData}
                  index={index}
                  brokenLimitFilter={brokenLimitFilter}
                  handleUpdateCampo={handleUpdateCampo}
                  handleBlur={(rowData: RowData) => handleBlur(rowData, index)}
                />
              );
            }}
          </Cell>
        </Column>

        {/* @ts-ignore */}
        <Column sortable width={116}>
          <HeaderCell className={styles['gerenciador-page__table__header-cell']}>
            <CustomHeaderCell
              dataKey={datakeys.newMargin}
              value={sortColumn}
              selectedValue={getActiveSelectedValue('newMargin')}
              data={sortOptions.newMargin}
              onChangeSort={onUpdateSortColumn}
            >
              Margem
            </CustomHeaderCell>
          </HeaderCell>
          <Cell className="table__input-group" dataKey={datakeys.newMargin}>
            {(rowData: RowData, index: number) => {
              return (
                <MargemCell
                  dataKey="novaMargem"
                  rowData={rowData}
                  index={index}
                  brokenLimitFilter={brokenLimitFilter}
                  handleUpdateCampo={handleUpdateCampo}
                  handleRestoreRow={handleRestoreRow}
                  handleBlur={(rowData: RowData) => handleBlur(rowData, index)}
                />
              );
            }}
          </Cell>
        </Column>

        {/* @ts-ignore */}
        <Column width={140}>
          <HeaderCell className={styles['gerenciador-page__table__header-cell']}>
            <Whisper placement="bottom" speaker={<Tooltip>Previsão de receita para 28 dias</Tooltip>}>
              <p>Previsão</p>
            </Whisper>
          </HeaderCell>
          <Cell dataKey="demandForecast">{(rowData: RowData, index: number) => <DemandForecastCell rowData={rowData} index={index} />}</Cell>
        </Column>

        {/* @ts-ignore */}
        <Column width={26}>
          <HeaderCell />
          <Cell dataKey="wholesale">
            {(rowData: RowData) => {
              return (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <MemoExpandWholesaleCell rowData={rowData}>2</MemoExpandWholesaleCell>
                  {isPriceOfferEnabled && <MemoExpandOfferCell rowData={rowData}>3</MemoExpandOfferCell>}
                </div>
              );
            }}
          </Cell>
        </Column>
      </Table>

      {selectedRowData && (
        <GetClusterListStoreModal
          rowData={selectedRowData}
          show={showModalClusterStore}
          onHide={() => {
            setShowModalClusterStore(false);
            setSelectedRowData(null);
            dispatch(SET_GET_CLUSTER_STORE_MODAL_IS_OPEN(false));
          }}
        />
      )}
      {selectedRowData && datapointExhibitionType === 'PRODUCT_CLUSTER_FAMILY' && (
        <GetStoreFamilyClusterModal
          rowData={selectedRowData}
          show={showModalProductFamilyCluster}
          onHide={() => {
            setShowModalProductFamilyCluster(false);
            setSelectedRowData(null);
            dispatch(SET_GET_CLUSTER_STORE_MODAL_IS_OPEN(false));
          }}
        />
      )}
    </>
  );
};

export default memo(TableGerenciador);
